import { useState, type KeyboardEvent } from "react";
import { useTranslate } from "ra-core";
import { Ban, Loader2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  CHANNEL_HUES,
  channelColorStyle,
  DEFAULT_SHADE,
  hueOfColor,
  SHADE_NAMES,
} from "./channelColors";

export type ChannelDraft = { name: string; color: string | null };

/**
 * A channel's name and colour: to create one (from the note form's picker or
 * from Settings) or to edit one. LCCRM-87.
 *
 * The colour is picked in two steps, a hue and then one of that hue's shades,
 * all presets; "none" leaves the plain label.
 *
 * `onSubmit` rejects with a message to show under the field; the dialog stays
 * open so the name can be fixed. Not a <form>: it opens inside the note form,
 * and a nested form's submit would bubble out and save the note. Every button
 * here is `type="button"` for the same reason.
 */
export const ChannelDialog = ({
  title,
  initialName = "",
  initialColor = null,
  confirmLabel,
  onSubmit,
  onClose,
}: {
  title: string;
  initialName?: string;
  initialColor?: string | null;
  confirmLabel: string;
  onSubmit: (channel: ChannelDraft) => Promise<void>;
  onClose: () => void;
}) => {
  const translate = useTranslate();
  const [name, setName] = useState(initialName);
  const [color, setColor] = useState<string | null>(initialColor);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hue = hueOfColor(color);
  const shade = hue ? hue.shades.indexOf(color!.toLowerCase()) : -1;

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit({ name: trimmed, color });
    } catch (cause) {
      setError(
        cause instanceof Error && cause.message
          ? cause.message
          : translate("resources.notes.channel.save_error", {
              _: "Couldn't save the channel. Try again.",
            }),
      );
      setBusy(false);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    void submit();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-1">
          <Input
            autoFocus
            value={name}
            aria-label={translate("resources.notes.channel.name", {
              _: "Channel name",
            })}
            aria-invalid={error !== null}
            placeholder={translate("resources.notes.channel.name_placeholder", {
              _: "For example WhatsApp",
            })}
            onChange={(event) => {
              setName(event.target.value);
              setError(null);
            }}
            onKeyDown={handleKeyDown}
          />
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium">
              {translate("resources.notes.channel.color", { _: "Color" })}
            </span>
            <Badge
              variant="outline"
              className="text-xs font-normal"
              style={channelColorStyle(color)}
            >
              {name.trim() ||
                translate("resources.notes.channel.placeholder", {
                  _: "Channel",
                })}
            </Badge>
          </div>
          <div
            role="radiogroup"
            aria-label={translate("resources.notes.channel.color", {
              _: "Color",
            })}
            className="flex flex-wrap gap-1.5"
          >
            <Swatch
              label={translate("resources.notes.channel.no_color", {
                _: "No color",
              })}
              selected={color === null}
              onSelect={() => setColor(null)}
            >
              <Ban className="size-3.5 text-muted-foreground" />
            </Swatch>
            {CHANNEL_HUES.map((option) => (
              <Swatch
                key={option.id}
                label={option.name}
                fill={option.shades[DEFAULT_SHADE]}
                selected={hue?.id === option.id}
                // Changing hue keeps the shade already chosen.
                onSelect={() =>
                  setColor(option.shades[shade >= 0 ? shade : DEFAULT_SHADE])
                }
              />
            ))}
          </div>
          {hue && (
            <div
              role="radiogroup"
              aria-label={translate("resources.notes.channel.shade", {
                _: "Shade",
              })}
              className="flex flex-wrap gap-1.5"
            >
              {hue.shades.map((option, index) => (
                <Swatch
                  key={option}
                  label={`${hue.name}, ${SHADE_NAMES[index]}`}
                  fill={option}
                  selected={index === shade}
                  onSelect={() => setColor(option)}
                />
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            {translate("ra.action.cancel")}
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={busy || name.trim() === ""}
          >
            {busy && <Loader2 className="size-4 animate-spin" />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Swatch = ({
  label,
  fill,
  selected,
  onSelect,
  children,
}: {
  label: string;
  fill?: string;
  selected: boolean;
  onSelect: () => void;
  children?: React.ReactNode;
}) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    aria-label={label}
    title={label}
    onClick={onSelect}
    style={fill ? { backgroundColor: fill } : undefined}
    className={cn(
      "flex size-6 items-center justify-center rounded-full border border-border outline-none transition-shadow",
      "focus-visible:ring-2 focus-visible:ring-ring",
      selected && "ring-2 ring-foreground ring-offset-2 ring-offset-background",
    )}
  >
    {children}
  </button>
);
